// El ejecutor de las herramientas del Catálogo (ADR-2026-10-14-el-agente-arma-el-catalogo-desde-
// la-pantalla): valida lo que escribió el modelo, lo aplica con las MISMAS acciones que usan los
// botones (`usePlanDeSesion`) y le cuenta en texto qué pasó y qué falta. Puro salvo lo que
// delega en `acciones`.
import type { ResultadoDeHerramientaDePantalla } from '../../../../../chat-atlas';
import { fusionarFicha, type FichaDeProducto } from '../../../../../lib/fichaDeProducto';
import { normalizarMedidas, type Medidas } from '../../../../../lib/medidas';
import { normalizarResolucion, type ResolucionImagen } from '../../../../../lib/resolucionImagen';
import { FORMAT_OPTIONS_V2 } from '../../../mockData';
import type { ResultadoDeEscritura } from '../escritorDeCatalogo';
import type { ResultadoDeGeneracion } from '../generarCatalogo';
import {
  COLORES_DE_INFOGRAFIA,
  ICONOS_DE_TOMA,
  MAX_CANTIDAD_TOMA,
  MAX_PRODUCTOS as MAX_PRODUCTOS_DEL_PLAN,
  MAX_TOMAS,
  MAX_INSTRUCCION_EDICION,
  aReescribir,
  costoDelPlan,
  costoPorLanzar,
  imagenesPorLanzar,
  normalizarToma,
  queFalta,
  totalImagenes,
  tomasConFoto,
  itemsDelFiltro,
  type FiltroDeItems,
  type ItemDelPlan,
  MAX_PROMPT_DE_TOMA,
  normalizarEtiquetas,
  type PlanDeCatalogo,
  type ProductoParaElPlan,
  type ProductosSumados,
  type ReferenciaDelPlan,
  type TomaDelPlan,
} from '../plan';
import { MAX_TOMAS_POR_VISTA, NOMBRES_HERRAMIENTAS_DEL_CATALOGO } from './herramientas';

/** Lo que la sesión sabe hacer (las acciones de `usePlanDeSesion` que el agente usa). */
export interface AccionesDelCatalogo {
  leerPlan: () => PlanDeCatalogo;
  reemplazarTomas: (tomas: TomaDelPlan[]) => void;
  setTitulo: (t: string) => void;
  setEstilo: (e: string, opts?: { reescribir?: boolean }) => void;
  setFormato: (slug: string) => void;
  setResolucion: (r: ResolucionImagen) => void;
  agregarToma: (toma: TomaDelPlan) => void;
  quitarToma: (id: string) => void;
  editarToma: (id: string, parcial: Partial<Omit<TomaDelPlan, 'id'>>) => void;
  quitarProducto: (varianteId: string) => void;
  /** Saca UNA toma de UN producto (el "Eliminar" del panel del ítem). */
  quitarItem: (varianteId: string, tomaId: string) => void;
  /** Las URLs (firmadas, chicas) de fotos generadas, para mostrárselas al modelo. */
  firmarFotos: (imageIds: string[]) => Promise<Map<string, string>>;
  sumarProductos: (productos: ProductoParaElPlan[], tomaIds?: string[]) => ProductosSumados;
  /** Los productos del catálogo de la marca con sus medidas y su ficha, listos para el plan. */
  buscarProductos: (ids: string[]) => Promise<{ encontrados: ProductoParaElPlan[]; noEncontrados: string[] }>;
  setNotas: (varianteId: string, notas: string) => void;
  setEtiquetas: (varianteId: string, etiquetas: string[]) => void;
  /** Suma una imagen como referencia del plan, o le cambia la etiqueta si ya está
   *  (ADR-2026-10-15-la-plantilla-de-marca). */
  ponerReferencia: (ref: ReferenciaDelPlan) => void;
  /** La foto que la persona subió al chat con ese `adjunto_id` ("m3-1"), o null. */
  adjuntoDelChat: (id: string) => { storagePath: string; bucket?: string } | null;
  setMedidas: (varianteId: string, medidas: Medidas) => void;
  setFicha: (varianteId: string, ficha: FichaDeProducto) => void;
  editarPrompt: (itemId: string, prompt: string) => void;
  escribir: (solo?: FiltroDeItems, indicacion?: string) => Promise<ResultadoDeEscritura>;
  marcarEdicion: (solo: FiltroDeItems | undefined, instruccion: string) => { marcados: string[]; sinFoto: number };
  marcarRehacer: (solo: FiltroDeItems | undefined) => { marcados: string[] };
  quitarPendientes: (solo?: FiltroDeItems) => void;
  pedirGenerar: () => Promise<ResultadoDeGeneracion>;
}

export interface ContextoDelCatalogo {
  plan: PlanDeCatalogo;
}

const ok = (texto: string): ResultadoDeHerramientaDePantalla => ({ content: texto });
const error = (texto: string): ResultadoDeHerramientaDePantalla => ({ content: texto, is_error: true });
const CIERRE = 'Para ver el plan con los ids: ver_pantalla.';
/** Un cambio de estilo o de tomas no reescribe las tomas que ya tienen foto
 *  (ADR-2026-10-15-una-toma-con-foto-no-pierde-su-prompt): el modelo tiene que saberlo para
 *  ofrecerlo en vez de dar el catálogo por actualizado. */
const avisoConFoto = (plan: PlanDeCatalogo): string => {
  const n = tomasConFoto(plan);
  if (n === 0) return '';
  return ` ${n === 1 ? '1 toma con foto conserva su prompt y su foto' : `${n} tomas con foto conservan su prompt y su foto`} anteriores: no se reescriben solas. Si la persona quiere el cambio también en ${n === 1 ? 'ésa' : 'ésas'}, ofrecéselo: escribir_prompts con aunque_tengan_foto: true y Generar las rehace (cuesta regenerarlas).`;
};
const ESTADO_DEL_ITEM: Record<ItemDelPlan['estado'], string> = {
  'sin-prompt': 'sin prompt',
  escrito: 'prompt listo',
  generando: 'generando',
  generada: 'generada',
  fallo: 'falló',
};

const str = (v: unknown, max = 10_000): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const aKebab = (v: unknown): string =>
  str(v, 60)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

const esFormato = (v: unknown): v is string => typeof v === 'string' && FORMAT_OPTIONS_V2.some((f) => f.id === v);
const esResolucion = (v: unknown): v is ResolucionImagen => v === '1K' || v === '2K' || v === '4K';

/** Una toma tal como la escribe el modelo (snake_case) → `TomaDelPlan`, con avisos. */
function tomaDelModelo(raw: unknown, plan: PlanDeCatalogo, avisos: string[]): TomaDelPlan | null {
  const o = (raw ?? {}) as Record<string, unknown>;
  const id = aKebab(o.id) || aKebab(o.titulo);
  const titulo = str(o.titulo, 60);
  if (!id || !titulo) {
    avisos.push('una toma sin id o sin título se descartó');
    return null;
  }
  const icono = str(o.icono);
  if (icono && !(ICONOS_DE_TOMA as readonly string[]).includes(icono)) avisos.push(`el ícono "${icono}" no existe: la toma ${titulo} va con "otro"`);
  const ids = new Set(plan.productos.map((p) => p.varianteId));
  let aplicaA: 'todos' | string[] = 'todos';
  if (Array.isArray(o.aplica_a)) {
    const pedidos = o.aplica_a.filter((x): x is string => typeof x === 'string');
    const ajenos = pedidos.filter((x) => !ids.has(x));
    if (ajenos.length > 0) avisos.push(`en ${titulo}, estos ids no están en el plan y se ignoraron: ${ajenos.join(', ')}`);
    const validos = pedidos.filter((x) => ids.has(x));
    aplicaA = validos.length > 0 ? validos : 'todos';
    if (validos.length === 0 && pedidos.length > 0) avisos.push(`${titulo} quedó para todos los productos`);
  }
  const cantidadPedida = typeof o.cantidad === 'number' ? Math.round(o.cantidad) : 1;
  if (cantidadPedida > MAX_CANTIDAD_TOMA) avisos.push(`${titulo}: hasta ${MAX_CANTIDAD_TOMA} imágenes por producto`);
  const colorInfografia = str(o.color_infografia);
  if (colorInfografia && !(COLORES_DE_INFOGRAFIA as readonly string[]).includes(colorInfografia)) {
    avisos.push(`el fondo de infografía "${colorInfografia}" no existe (blanco, grafito o beige): ${titulo} va en blanco`);
  }
  const toma = normalizarToma({
    id,
    titulo,
    icono: icono || 'otro',
    queMuestra: str(o.que_muestra, 600),
    aplicaA,
    cantidad: cantidadPedida,
    fondoBlanco: o.fondo_blanco === true,
    luzFondoBlanco: o.luz_fondo_blanco,
    tarea: o.tarea,
    referencias: Array.isArray(o.referencias) ? o.referencias.filter((r) => plan.referencias.some((x) => x.id === r)) : undefined,
    infografia: o.infografia === true,
    pillsDeLaFicha: o.pills_de_la_ficha,
    colorInfografia,
    estiloGrafico: o.estilo_grafico,
  });
  return toma;
}

export interface PropuestaNormalizada {
  titulo: string;
  estilo: string;
  formato?: string;
  resolucion?: ResolucionImagen;
  tomas: TomaDelPlan[];
  avisos: string[];
}

/** Lo que escribió el modelo en `proponer_plan` → validado, o un error con el motivo. Puro. */
export function normalizarPropuesta(input: unknown, plan: PlanDeCatalogo): PropuestaNormalizada | { error: string } {
  const o = (input ?? {}) as Record<string, unknown>;
  const avisos: string[] = [];
  const crudas = Array.isArray(o.tomas) ? o.tomas : [];
  if (crudas.length === 0) return { error: 'El plan necesita al menos una toma.' };
  if (crudas.length > MAX_TOMAS) return { error: `Hasta ${MAX_TOMAS} tomas por catálogo (mandaste ${crudas.length}).` };
  const vistos = new Set<string>();
  const tomas: TomaDelPlan[] = [];
  for (const c of crudas) {
    const t = tomaDelModelo(c, plan, avisos);
    if (!t) continue;
    if (vistos.has(t.id)) {
      avisos.push(`la toma "${t.id}" estaba repetida: se tomó la primera`);
      continue;
    }
    vistos.add(t.id);
    tomas.push(t);
  }
  if (tomas.length === 0) return { error: 'Ninguna toma tenía id y título.' };
  if (o.formato !== undefined && !esFormato(o.formato)) avisos.push(`el formato "${String(o.formato)}" no existe: queda el actual`);
  if (o.resolucion !== undefined && !esResolucion(o.resolucion)) avisos.push(`la calidad "${String(o.resolucion)}" no existe: queda la actual`);
  return {
    titulo: str(o.titulo, 80) || plan.titulo,
    estilo: str(o.estilo, 1500),
    ...(esFormato(o.formato) ? { formato: o.formato } : {}),
    ...(esResolucion(o.resolucion) ? { resolucion: o.resolucion } : {}),
    tomas,
    avisos,
  };
}

type Operacion = (op: Record<string, unknown>, plan: PlanDeCatalogo, acciones: AccionesDelCatalogo) => string;
class Rechazo extends Error {}

/** Una operación de `modificar_plan`. Devuelve qué hizo; tira `Rechazo` con el motivo. */
const OPERACIONES: Record<string, Operacion> = {
  // Una imagen del plan o del chat, con QUÉ ES: "Plantilla: …" la vuelve la plantilla de la marca
  // (ADR-2026-10-15-la-plantilla-de-marca). Después, editar_toma la suma a las tomas que la usan.
  referencia: (op, plan, a) => {
    const id = str(op.referencia_id, 40);
    const etiqueta = str(op.etiqueta, 120);
    const actual = plan.referencias.find((r) => r.id === id);
    if (actual) {
      a.ponerReferencia({ ...actual, etiqueta });
      return `referencia ${id}: «${etiqueta || actual.etiqueta}»`;
    }
    const adjunto = a.adjuntoDelChat(id);
    if (!adjunto) throw new Rechazo(`no hay una referencia ni una foto del chat "${id}"`);
    a.ponerReferencia({ id, etiqueta, storagePath: adjunto.storagePath, ...(adjunto.bucket ? { bucket: adjunto.bucket } : {}) });
    return `referencia ${id} sumada al plan: «${etiqueta || 'Referencia'}»`;
  },
  estilo: (op, _plan, a) => {
    // El estilo manda sobre todos los prompts: lo escrito y no fijado se reescribe.
    a.setEstilo(str(op.valor, 1500), { reescribir: true });
    return `estilo (los prompts escritos sin foto y no fijados se reescriben).${avisoConFoto(a.leerPlan())}`;
  },
  titulo: (op, _plan, a) => {
    const valor = str(op.valor, 80);
    if (!valor) throw new Rechazo('titulo vacío');
    a.setTitulo(valor);
    return 'título';
  },
  formato: (op, _plan, a) => {
    const valor = str(op.valor);
    if (!esFormato(valor)) throw new Rechazo(`formato "${valor}" no existe`);
    a.setFormato(valor);
    return `formato ${valor}`;
  },
  resolucion: (op, _plan, a) => {
    const valor = str(op.valor);
    if (!esResolucion(valor)) throw new Rechazo(`calidad "${valor}" no existe`);
    a.setResolucion(normalizarResolucion(valor));
    return `calidad ${valor}`;
  },
  agregar_toma: (op, plan, a) => {
    if (plan.tomas.length >= MAX_TOMAS) throw new Rechazo(`ya hay ${MAX_TOMAS} tomas`);
    const avisos: string[] = [];
    const t = tomaDelModelo(op.toma, plan, avisos);
    if (!t) throw new Rechazo(`agregar_toma: ${avisos.join('; ') || 'toma inválida'}`);
    if (plan.tomas.some((x) => x.id === t.id)) throw new Rechazo(`la toma "${t.id}" ya existe (usá editar_toma)`);
    a.agregarToma(t);
    return `toma ${t.titulo} agregada${avisos.length ? ` (${avisos.join('; ')})` : ''}`;
  },
  quitar_toma: (op, plan, a) => {
    const tomaId = aKebab(op.toma_id);
    if (!plan.tomas.some((t) => t.id === tomaId)) throw new Rechazo(`no hay una toma "${tomaId || str(op.toma_id)}"`);
    a.quitarToma(tomaId);
    return `toma ${tomaId} quitada`;
  },
  editar_toma: (op, plan, a) => {
    const base = (op.toma ?? {}) as Record<string, unknown>;
    const idEdit = aKebab(op.toma_id) || aKebab(base.id);
    const actual = plan.tomas.find((t) => t.id === idEdit);
    if (!actual) throw new Rechazo(`no hay una toma "${idEdit || '?'}"`);
    const avisos: string[] = [];
    const fundida = tomaDelModelo(
      {
        id: actual.id,
        titulo: base.titulo ?? actual.titulo,
        icono: base.icono ?? actual.icono,
        que_muestra: base.que_muestra ?? actual.queMuestra,
        aplica_a: base.aplica_a ?? (actual.aplicaA === 'todos' ? undefined : actual.aplicaA),
        cantidad: base.cantidad ?? actual.cantidad,
        fondo_blanco: base.fondo_blanco ?? actual.fondoBlanco,
        luz_fondo_blanco: base.luz_fondo_blanco ?? actual.luzFondoBlanco,
        tarea: base.tarea ?? actual.tarea,
        referencias: base.referencias ?? actual.referencias,
        infografia: base.infografia ?? actual.infografia,
        pills_de_la_ficha: base.pills_de_la_ficha ?? actual.pillsDeLaFicha,
        color_infografia: base.color_infografia ?? actual.colorInfografia,
        estilo_grafico: base.estilo_grafico ?? actual.estiloGrafico,
      },
      plan,
      avisos,
    );
    if (!fundida) throw new Rechazo(`editar_toma ${idEdit}: ${avisos.join('; ')}`);
    const parcial: Partial<Omit<TomaDelPlan, 'id'>> = {};
    for (const k of ['titulo', 'icono', 'queMuestra', 'aplicaA', 'cantidad', 'fondoBlanco', 'luzFondoBlanco', 'tarea', 'referencias', 'infografia', 'pillsDeLaFicha', 'colorInfografia', 'estiloGrafico'] as const) {
      if (JSON.stringify(fundida[k]) !== JSON.stringify(actual[k])) (parcial as Record<string, unknown>)[k] = fundida[k];
    }
    a.editarToma(actual.id, parcial);
    return `toma ${actual.titulo} editada${avisos.length ? ` (${avisos.join('; ')})` : ''}`;
  },
  quitar_producto: (op, plan, a) => {
    const id = str(op.producto_id);
    if (!plan.productos.some((p) => p.varianteId === id)) throw new Rechazo(`no hay un producto "${id}"`);
    a.quitarProducto(id);
    return `producto ${id} quitado`;
  },
  quitar_de_producto: (op, plan, a) => {
    const id = str(op.producto_id);
    const tomaId = aKebab(op.toma_id);
    const producto = plan.productos.find((p) => p.varianteId === id);
    if (!producto) throw new Rechazo(`no hay un producto "${id}"`);
    const item = producto.items.find((i) => i.tomaId === tomaId);
    if (!item) throw new Rechazo(`${producto.nombre} no tiene la toma "${tomaId || str(op.toma_id)}"`);
    if (item.estado === 'generando') throw new Rechazo(`${producto.nombre} · ${tomaId} se está generando: esperá a que salga`);
    a.quitarItem(id, tomaId);
    return `toma ${tomaId} sacada de ${producto.nombre}${item.imageIds?.length ? ' (la foto queda en Creaciones)' : ''}`;
  },
  notas_de_producto: (op, plan, a) => {
    const id = str(op.producto_id);
    const producto = plan.productos.find((p) => p.varianteId === id);
    if (!producto) throw new Rechazo(`no hay un producto "${id}"`);
    a.setNotas(id, str(op.notas, 600));
    return `notas de ${producto.nombre}`;
  },
  etiquetas_de_producto: (op, plan, a) => {
    const id = str(op.producto_id);
    const producto = plan.productos.find((p) => p.varianteId === id);
    if (!producto) throw new Rechazo(`no hay un producto "${id}"`);
    const etiquetas = normalizarEtiquetas(op.etiquetas);
    a.setEtiquetas(id, etiquetas);
    return etiquetas.length
      ? `etiquetas de la ficha de ${producto.nombre}: ${etiquetas.join(' · ')} (reescribí su ficha de medidas con escribir_prompts)`
      : `${producto.nombre} vuelve a las etiquetas de su ficha`;
  },
};

function aplicarOperacion(op: Record<string, unknown>, acciones: AccionesDelCatalogo, hechas: string[], rechazadas: string[]): void {
  const tipo = str(op.op);
  const fn = OPERACIONES[tipo];
  if (!fn) {
    rechazadas.push(`la operación "${tipo}" no existe`);
    return;
  }
  try {
    hechas.push(fn(op, acciones.leerPlan(), acciones));
  } catch (err) {
    if (err instanceof Rechazo) rechazadas.push(err.message);
    else throw err;
  }
}

const resumenDelPlan = (plan: PlanDeCatalogo): string => {
  const faltas = queFalta(plan);
  const total = totalImagenes(plan);
  const porLanzar = imagenesPorLanzar(plan);
  return `${plan.tomas.length} toma${plan.tomas.length === 1 ? '' : 's'} (${plan.tomas.map((t) => t.titulo).join(', ')}) para ${plan.productos.length} producto${plan.productos.length === 1 ? '' : 's'}: ${total} imagen${total === 1 ? '' : 'es'}, ${costoDelPlan(plan)} créditos; Generar lanza ${porLanzar} (${costoPorLanzar(plan)} créditos).${faltas.length ? ` Falta: ${faltas.join('; ')}.` : ' No falta nada.'}`;
};

const listaDeIds = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined);
const filtroDe = (v: unknown): FiltroDeItems | undefined => {
  const o = (v ?? undefined) as Record<string, unknown> | undefined;
  return o && typeof o === 'object' ? { items: listaDeIds(o.items), productos: listaDeIds(o.productos), tomas: listaDeIds(o.tomas) } : undefined;
};
const fotos = (n: number) => `${n} foto${n === 1 ? '' : 's'}`;

export async function ejecutarHerramientaDelCatalogo(
  nombre: string,
  input: unknown,
  ctx: ContextoDelCatalogo,
  acciones: AccionesDelCatalogo,
): Promise<ResultadoDeHerramientaDePantalla> {
  if (!NOMBRES_HERRAMIENTAS_DEL_CATALOGO.includes(nombre)) return error(`La herramienta ${nombre} no es del Catálogo.`);
  const o = (input ?? {}) as Record<string, unknown>;
  try {
    switch (nombre) {
      case 'proponer_plan': {
        const p = normalizarPropuesta(input, ctx.plan);
        if ('error' in p) return error(p.error);
        acciones.setTitulo(p.titulo);
        acciones.setEstilo(p.estilo, { reescribir: true });
        if (p.formato) acciones.setFormato(p.formato);
        if (p.resolucion) acciones.setResolucion(p.resolucion);
        acciones.reemplazarTomas(p.tomas);
        const plan = acciones.leerPlan();
        return ok(`Plan armado: ${resumenDelPlan(plan)}${p.avisos.length ? ` Avisos: ${p.avisos.join('; ')}.` : ''}${avisoConFoto(plan)} Ahora escribí los prompts con escribir_prompts. ${CIERRE}`);
      }
      case 'modificar_plan': {
        const ops = Array.isArray(o.operaciones) ? (o.operaciones as Array<Record<string, unknown>>) : [];
        if (ops.length === 0) return error('modificar_plan necesita al menos una operación.');
        const hechas: string[] = [];
        const rechazadas: string[] = [];
        for (const op of ops) aplicarOperacion(op, acciones, hechas, rechazadas);
        const plan = acciones.leerPlan();
        return ok(
          `${hechas.length ? `Hecho: ${hechas.join('; ')}.` : 'No se aplicó nada.'}${rechazadas.length ? ` Rechazado: ${rechazadas.join('; ')}.` : ''} Ahora: ${resumenDelPlan(plan)}${queFalta(plan).some((f) => f.includes('prompt')) ? ' Llamá escribir_prompts.' : ''} ${CIERRE}`,
        );
      }
      case 'sumar_productos': {
        const ids = [...new Set(listaDeIds(o.product_ids) ?? [])];
        if (ids.length === 0) return error('sumar_productos necesita al menos un product_id.');
        const pedidas = listaDeIds(o.tomas);
        const tomaIds = pedidas && pedidas.length > 0 ? pedidas : undefined;
        const antes = acciones.leerPlan();
        const desconocidas = (tomaIds ?? []).filter((id) => !antes.tomas.some((t) => t.id === id));
        if (desconocidas.length) return error(`Esas tomas no están en el plan: ${desconocidas.join(', ')}. Las que hay: ${antes.tomas.map((t) => t.id).join(', ') || 'ninguna'}.`);
        const { encontrados, noEncontrados } = await acciones.buscarProductos(ids);
        if (encontrados.length === 0) return error(`No encontré ${ids.length === 1 ? 'ese producto' : 'esos productos'} en el catálogo de la marca. Si mandó fotos de un producto nuevo, crealo primero con agregar_productos.`);
        const r = acciones.sumarProductos(encontrados, tomaIds);
        const nombre = (id: string) => encontrados.find((p) => p.varianteId === id)?.nombre ?? id;
        const plan = acciones.leerPlan();
        const partes = [
          r.sumados.length ? `Sumé ${r.sumados.map(nombre).join(', ')}${tomaIds ? ` con las tomas ${tomaIds.join(', ')}` : ' con todas las tomas'}.` : 'No sumé nada.',
          r.yaEstaban.length ? `Ya estaban: ${[...new Set(r.yaEstaban)].map(nombre).join(', ')}.` : '',
          noEncontrados.length ? `No están en el catálogo de la marca: ${noEncontrados.join(', ')}.` : '',
          r.sinLugar.length ? `No entraron por el tope de ${MAX_PRODUCTOS_DEL_PLAN} productos: ${r.sinLugar.map(nombre).join(', ')}.` : '',
          `Ahora: ${resumenDelPlan(plan)}`,
          r.sumados.length && plan.tomas.length ? `Escribí sus prompts con escribir_prompts y solo.productos = ${JSON.stringify(r.sumados)}.` : '',
          CIERRE,
        ];
        return ok(partes.filter(Boolean).join(' '));
      }
      case 'cargar_datos_de_productos': {
        const lista = Array.isArray(o.productos) ? (o.productos as Array<Record<string, unknown>>) : [];
        if (lista.length === 0) return error('cargar_datos_de_productos necesita al menos un producto.');
        const sobrescribir = o.sobrescribir === true;
        const guardados: string[] = [];
        const rechazados: string[] = [];
        for (const entrada of lista) {
          const plan = acciones.leerPlan();
          const id = str(entrada.producto_id);
          const producto = plan.productos.find((p) => p.varianteId === id);
          if (!producto) {
            rechazados.push(`"${id}" no está en el plan`);
            continue;
          }
          const partes: string[] = [];
          if (entrada.medidas && typeof entrada.medidas === 'object') {
            const m = normalizarMedidas({ unidad: 'cm', ...(producto.medidas ?? {}), ...(entrada.medidas as Record<string, unknown>) });
            if (m) {
              if (producto.medidas?.origen === 'usuario' && !sobrescribir) rechazados.push(`${producto.nombre}: ya tiene medidas confirmadas por la persona (pedí sobrescribir)`);
              else {
                acciones.setMedidas(id, { ...m, origen: 'usuario' });
                partes.push('medidas');
              }
            }
          }
          if (Array.isArray(entrada.campos)) {
            const nuevos = (entrada.campos as Array<Record<string, unknown>>).map((c) => ({ clave: str(c.clave, 40), valor: str(c.valor, 200) })).filter((c) => c.clave && c.valor);
            if (nuevos.length > 0) {
              const r = fusionarFicha(producto.ficha, nuevos, { origen: 'usuario', sobrescribir });
              acciones.setFicha(id, r.ficha);
              const fuera = new Set([...r.rechazados, ...r.sinLugar]);
              const aceptados = nuevos.filter((n) => !fuera.has(n.clave.toLowerCase()));
              if (aceptados.length) partes.push(aceptados.map((n) => n.clave).join(', '));
              if (r.rechazados.length) rechazados.push(`${producto.nombre}: ${r.rechazados.join(', ')} ya confirmado por la persona (pedí sobrescribir)`);
              if (r.sinLugar.length) rechazados.push(`${producto.nombre}: ${r.sinLugar.join(', ')} no entra (la ficha ya tiene el máximo de características)`);
            }
          }
          if (partes.length) guardados.push(`${producto.nombre}: ${partes.join(', ')}`);
        }
        return ok(
          `${guardados.length ? `Guardado en la ficha: ${guardados.join('; ')}.` : 'No se guardó nada.'}${rechazados.length ? ` Rechazado: ${rechazados.join('; ')}.` : ''}${
            guardados.length ? ' Los prompts no cambian solos: reescribí sólo las tomas que MUESTRAN esos datos (una ficha, una lámina, una infografía), y si ya tienen foto preguntá antes, porque quedan para regenerar.' : ''
          } ${CIERRE}`,
        );
      }
      case 'escribir_prompts': {
        const indicacion = str(o.indicacion, 600);
        const solo = filtroDe(o.solo);
        // Reescribir una toma que ya tiene foto la deja para regenerar (y cobra): sólo con
        // `aunque_tengan_foto` (ADR-2026-10-15-reescribir-una-toma-con-foto-es-explicito).
        const { sinFoto, conFoto, creditos } = aReescribir(acciones.leerPlan(), solo);
        const conFotoTambien = o.aunque_tengan_foto === true;
        const ids = (items: { id: string }[]) => items.map((i) => i.id).join(', ');
        if (conFoto.length > 0 && !conFotoTambien && sinFoto.length === 0) {
          return ok(
            `No reescribí nada: ${conFoto.length === 1 ? 'la toma pedida ya tiene foto' : `las ${conFoto.length} tomas pedidas ya tienen foto`} (${ids(conFoto)}). Reescribirlas las deja para regenerar: Generar las vuelve a hacer (${creditos} créditos). Si la persona lo quiere, volvé a llamar con aunque_tengan_foto: true; si sólo cargaste datos que esas tomas no muestran, no hace falta. ${CIERRE}`,
          );
        }
        const filtro = conFoto.length > 0 && !conFotoTambien ? { items: sinFoto.map((i) => i.id) } : solo;
        const r = await acciones.escribir(filtro, indicacion || undefined);
        const fallidos = r.fallidos.length ? ` No salieron ${r.fallidos.length}: ${r.fallidos.map((f) => `${f.producto} (${f.motivo})`).join('; ')}.` : '';
        const salteadas =
          conFoto.length > 0 && !conFotoTambien
            ? ` No toqué ${conFoto.length} que ya tienen foto (${ids(conFoto)}): reescribirlas cuesta regenerarlas (${creditos} créditos); con aunque_tengan_foto: true, si la persona lo pide.`
            : '';
        const aRegenerar =
          conFoto.length > 0 && conFotoTambien
            ? ` ${conFoto.length} de las reescritas ya tenían foto: quedan POR REGENERAR (la foto vieja sigue hasta que salga la nueva) y Generar las rehace por ${creditos} créditos. Decíselo así: no son fotos nuevas.`
            : '';
        return ok(`Prompts escritos: ${r.escritos}.${fallidos}${salteadas}${aRegenerar} ${resumenDelPlan(acciones.leerPlan())} ${CIERRE}`);
      }
      case 'ver_tomas': {
        const plan = acciones.leerPlan();
        const filtro = filtroDe(o.solo);
        const todos = itemsDelFiltro(plan, filtro);
        if (todos.length === 0) return error(`No hay tomas en lo que pediste. ${CIERRE}`);
        const desde = Math.max(1, Math.round(typeof o.desde === 'number' ? o.desde : 1));
        const pagina = todos.slice(desde - 1, desde - 1 + MAX_TOMAS_POR_VISTA);
        if (pagina.length === 0) return error(`Hay ${todos.length} tomas en lo que pediste: \`desde\` va de 1 a ${todos.length}.`);
        const conFotos = o.sin_fotos !== true;
        const fotoDe = (i: ItemDelPlan) => (i.estado === 'generada' ? i.imageIds?.[0] : undefined);
        const urls = conFotos ? await acciones.firmarFotos(pagina.map(fotoDe).filter((x): x is string => !!x)).catch(() => new Map<string, string>()) : new Map<string, string>();
        const nombreDe = new Map(plan.productos.map((p) => [p.varianteId, p.nombre]));
        const tituloDe = new Map(plan.tomas.map((t) => [t.id, t.titulo]));
        const hasta = desde - 1 + pagina.length;
        const bloques: unknown[] = [
          {
            type: 'text',
            text: `Tomas ${desde} a ${hasta} de ${todos.length}${hasta < todos.length ? ` (para seguir: desde=${hasta + 1})` : ''}. Cada una es un renglón con su item_id y su prompt${conFotos ? ', y JUSTO DEBAJO su foto si ya salió' : ''}.`,
          },
        ];
        for (const i of pagina) {
          const varianteId = i.id.slice(0, i.id.length - i.tomaId.length - 2);
          const foto = fotoDe(i);
          bloques.push({
            type: 'text',
            text: `${nombreDe.get(varianteId) ?? varianteId} · ${tituloDe.get(i.tomaId) ?? i.tomaId} · item_id=${i.id} · ${ESTADO_DEL_ITEM[i.estado]}${i.fijado ? ', fijado' : ''}${i.edicion ? `, edición pendiente: «${i.edicion.instruccion}»` : i.rehacer ? ', rehacer pendiente' : ''}${i.estado === 'escrito' && i.imageIds?.length ? ', la foto de la tarjeta es del prompt anterior (Generar la rehace)' : ''}${i.motivo ? ` · motivo: ${i.motivo}` : ''}\nPrompt: ${i.prompt.trim() ? `«${i.prompt}»` : '(sin escribir)'}${foto ? (urls.has(foto) ? `\nSu foto (image_id=${foto}):` : `\nTiene foto (image_id=${foto}) pero no se pudo cargar.`) : ''}`,
          });
          if (foto && urls.has(foto)) bloques.push({ type: 'image', source: { type: 'url', url: urls.get(foto)! } });
        }
        return { content: bloques };
      }
      case 'editar_prompt': {
        const itemId = str(o.item_id);
        const prompt = str(o.prompt, MAX_PROMPT_DE_TOMA);
        const item = acciones.leerPlan().productos.flatMap((p) => p.items).find((i) => i.id === itemId);
        if (!item) return error(`No hay un ítem "${itemId}". Los ids son producto__toma (ver_pantalla).`);
        if (item.estado === 'generando') return error(`${itemId} se está generando: esperá a que salga para cambiarle el prompt.`);
        if (!prompt) return error('El prompt no puede estar vacío.');
        const teniaFoto = item.estado === 'generada' || (item.imageIds?.length ?? 0) > 0;
        acciones.editarPrompt(itemId, prompt);
        return ok(
          `Prompt de ${itemId} reemplazado y fijado.${teniaFoto ? ' La foto actual sigue en la tarjeta y queda por regenerar con el prompt nuevo: sale cuando la persona toca Generar (decile el costo).' : ''} ${resumenDelPlan(acciones.leerPlan())} ${CIERRE}`,
        );
      }
      case 'editar_imagenes': {
        if (o.cancelar === true) {
          acciones.quitarPendientes(filtroDe(o.solo));
          return ok(`Saqué las ediciones y los rehacer pendientes. ${resumenDelPlan(acciones.leerPlan())} ${CIERRE}`);
        }
        const instruccion = str(o.instruccion, MAX_INSTRUCCION_EDICION);
        if (!instruccion) return error('editar_imagenes necesita la `instruccion`: qué cambiar en cada foto.');
        const r = acciones.marcarEdicion(filtroDe(o.solo), instruccion);
        if (r.marcados.length === 0) {
          return error(`No hay ninguna foto generada en lo que pediste${r.sinFoto ? ` (${r.sinFoto} toma${r.sinFoto === 1 ? '' : 's'} todavía sin foto: esas se generan con su prompt)` : ''}. ${CIERRE}`);
        }
        return ok(
          `Edición pendiente en ${fotos(r.marcados.length)}${r.sinFoto ? ` (${r.sinFoto} sin foto quedaron afuera)` : ''}: "${instruccion}". ${resumenDelPlan(acciones.leerPlan())} Para lanzarla, generar_catalogo (espera el click). ${CIERRE}`,
        );
      }
      case 'rehacer_imagenes': {
        if (o.cancelar === true) {
          acciones.quitarPendientes(filtroDe(o.solo));
          return ok(`Saqué las ediciones y los rehacer pendientes. ${resumenDelPlan(acciones.leerPlan())} ${CIERRE}`);
        }
        const r = acciones.marcarRehacer(filtroDe(o.solo));
        if (r.marcados.length === 0) return error(`No hay ninguna foto generada en lo que pediste. ${CIERRE}`);
        return ok(`${fotos(r.marcados.length)} para rehacer con su prompt. ${resumenDelPlan(acciones.leerPlan())} Para lanzarlas, generar_catalogo (espera el click). ${CIERRE}`);
      }
      case 'generar_catalogo': {
        const faltas = queFalta(ctx.plan);
        if (faltas.length > 0) return error(`Todavía no se puede generar. Falta: ${faltas.join('; ')}.`);
        if (imagenesPorLanzar(ctx.plan) === 0) {
          return error('No hay nada por lanzar: todo está generado o generando. Para cambiar fotos que ya salieron: editar_imagenes o rehacer_imagenes.');
        }
        const r = await acciones.pedirGenerar();
        if (!r.ok) return error(`No se generó: ${r.error}${r.lanzados ? ` (se lanzaron ${r.lanzados} imágenes antes)` : ''}.`);
        return ok(`Se lanzaron ${r.lanzados} imágenes en ${r.plan.lotes.length} lote${r.plan.lotes.length === 1 ? '' : 's'}; cada foto aparece en su tarjeta a medida que sale. ${CIERRE}`);
      }
      default:
        return error(`La herramienta ${nombre} no es del Catálogo.`);
    }
  } catch (err) {
    return error(`No se pudo ${nombre === 'escribir_prompts' ? 'escribir los prompts' : nombre === 'generar_catalogo' ? 'generar' : 'aplicar el cambio en el plan'}: ${err instanceof Error ? err.message : String(err)}`);
  }
}
